import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Chip, Alert, Tabs, Tab, InputAdornment, FormControl, InputLabel, Select, Card, CardContent, useMediaQuery, useTheme } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, VpnKey as KeyIcon, Search as SearchIcon, SwapHoriz as TransferIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function Users() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [targetManager, setTargetManager] = useState('');
  const [assignTab, setAssignTab] = useState(0);
  const [formData, setFormData] = useState({ email: '', password: '', full_name: '', role: 'manager', status: 'active' });
  const [newPassword, setNewPassword] = useState('');
  const [assignedCampaigns, setAssignedCampaigns] = useState([]);
  const [assignedPartners, setAssignedPartners] = useState([]);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');

  useEffect(() => { fetchUsers(); fetchCampaigns(); fetchPartners(); }, []);

  const fetchUsers = async () => {
    try { const res = await axios.get('/admin/users'); setUsers(res.data.data); }
    catch (err) { setError('Failed to load users'); }
    finally { setLoading(false); }
  };

  const fetchCampaigns = async () => {
    try { const res = await axios.get('/admin/campaigns'); setCampaigns(res.data.data); } catch {}
  };

  const fetchPartners = async () => {
    try { const res = await axios.get('/admin/partners'); setPartners(res.data.data); } catch {}
  };

  const filteredUsers = users
    .filter(u => u.role === 'admin' || u.role === 'manager' || u.role === 'media_buyer')
    .filter(u => searchTerm === '' || u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase()));

  const availableManagers = users.filter(u => (u.role === 'manager' || u.role === 'admin') && u.status === 'active' && u.user_id !== selectedUser?.user_id);

  const handleCreate = async () => {
    try {
      await axios.post('/admin/users', formData);
      setSuccess('User created'); setOpenDialog(false); fetchUsers();
      setFormData({ email: '', password: '', full_name: '', role: 'manager', status: 'active' });
    } catch (err) { setError(err.response?.data?.error || 'Failed to create user'); }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/admin/users/${selectedUser.user_id}`, formData);
      setSuccess('User updated'); setOpenDialog(false); fetchUsers();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update user'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await axios.delete(`/admin/users/${id}`); setSuccess('User deleted'); fetchUsers(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to delete user'); }
  };

  const handleChangePassword = async () => {
    try {
      await axios.put(`/admin/users/${selectedUser.user_id}/password`, { password: newPassword });
      setSuccess('Password changed'); setOpenPasswordDialog(false); setNewPassword('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to change password'); }
  };

  const handleTransferPartners = async () => {
    if (!targetManager) { setError('Please select a target manager'); return; }
    try {
      const currentPartnersRes = await axios.get(`/admin/users/${selectedUser.user_id}/partners`);
      const currentPartnerIds = currentPartnersRes.data.data.map(p => p.partner_id);
      if (currentPartnerIds.length === 0) { setError('This manager has no partners to transfer'); return; }
      const targetPartnersRes = await axios.get(`/admin/users/${targetManager}/partners`);
      const targetPartnerIds = targetPartnersRes.data.data.map(p => p.partner_id);
      const mergedPartnerIds = [...new Set([...targetPartnerIds, ...currentPartnerIds])];
      await axios.put(`/admin/users/${targetManager}/partners`, { partner_ids: mergedPartnerIds });
      await axios.put(`/admin/users/${selectedUser.user_id}/partners`, { partner_ids: [] });
      setSuccess(`Transferred ${currentPartnerIds.length} partner(s) successfully`);
      setOpenTransferDialog(false);
      setTargetManager('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to transfer partners'); }
  };

  const openEdit = (user) => { setSelectedUser(user); setFormData({ email: user.email, full_name: user.full_name, role: user.role, status: user.status }); setOpenDialog(true); };
  const openNew = () => { setSelectedUser(null); setFormData({ email: '', password: '', full_name: '', role: 'manager', status: 'active' }); setOpenDialog(true); };
  const openPassword = (user) => { setSelectedUser(user); setNewPassword(''); setOpenPasswordDialog(true); };
  const openTransfer = (user) => { setSelectedUser(user); setTargetManager(''); setOpenTransferDialog(true); };

  const openAssign = async (user) => {
    setSelectedUser(user); setAssignTab(0); setCampaignSearch(''); setPartnerSearch('');
    try {
      const [campRes, partRes] = await Promise.all([axios.get(`/admin/users/${user.user_id}/campaigns`), axios.get(`/admin/users/${user.user_id}/partners`)]);
      setAssignedCampaigns(campRes.data.data.map(c => c.campaign_id));
      setAssignedPartners(partRes.data.data.map(p => p.partner_id));
    } catch {}
    setOpenAssignDialog(true);
  };

  const handleSaveAssignments = async () => {
    try {
      await axios.put(`/admin/users/${selectedUser.user_id}/partners`, { partner_ids: assignedPartners });
      setSuccess('Assignments saved'); setOpenAssignDialog(false);
    } catch (err) { setError('Failed to save assignments'); }
  };
 
  const toggleCampaign = (id) => setAssignedCampaigns(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const togglePartner = (id) => setAssignedPartners(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const getRoleColor = (role) => { if (role === 'admin') return 'success'; if (role === 'manager') return 'warning'; if (role === 'media_buyer') return 'info'; return 'default'; };
  const formatRoleLabel = (role) => { if (role === 'media_buyer') return 'Media Buyer'; return role.charAt(0).toUpperCase() + role.slice(1); };

  const filteredCampaigns = campaigns.filter(c => campaignSearch === '' || c.name?.toLowerCase().includes(campaignSearch.toLowerCase()));
  const filteredPartners = partners.filter(p => partnerSearch === '' || p.name?.toLowerCase().includes(partnerSearch.toLowerCase()));

  if (!isAdmin()) return <Box p={3}><Alert severity="error">Access denied. Admin only.</Alert></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Users Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} size={isMobile ? 'small' : 'medium'}>Add User</Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <TextField size="small" placeholder="Search by name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ width: { xs: '100%', sm: 300 } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }} />
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {!isMobile && <TableCell>Email</TableCell>}
                <TableCell>Role</TableCell>
                {!isMobile && <TableCell>Status</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow><TableCell colSpan={isMobile ? 3 : 5} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>No users found</Typography></TableCell></TableRow>
              ) : filteredUsers.map((user) => (
                <TableRow key={user.user_id} hover sx={{ opacity: user.status === 'inactive' ? 0.6 : 1 }}>
                  <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{user.full_name}</TableCell>
                  {!isMobile && <TableCell sx={{ fontSize: '0.875rem' }}>{user.email}</TableCell>}
                  <TableCell><Chip label={formatRoleLabel(user.role)} color={getRoleColor(user.role)} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, color: 'white' }} /></TableCell>
                  {!isMobile && <TableCell><Chip label={user.status} color={user.status === 'active' ? 'success' : 'default'} size="small" variant="outlined" /></TableCell>}
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => openEdit(user)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => openPassword(user)} title="Change Password"><KeyIcon fontSize="small" /></IconButton>
                      {user.role === 'manager' && (
                        <>
                          {!isMobile && <Button size="small" onClick={() => openAssign(user)} sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}>Assign</Button>}
                          {isMobile && <IconButton size="small" onClick={() => openAssign(user)} title="Assign" color="primary"><AddIcon fontSize="small" /></IconButton>}
                          <IconButton size="small" onClick={() => openTransfer(user)} title="Transfer Partners" color="primary"><TransferIcon fontSize="small" /></IconButton>
                        </>
                      )}
                      <IconButton size="small" onClick={() => handleDelete(user.user_id)} color="error" title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{selectedUser ? 'Edit User' : 'Create User'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Full Name" size={isMobile ? 'small' : 'medium'} value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} margin="normal" />
          <TextField fullWidth label="Email" size={isMobile ? 'small' : 'medium'} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} margin="normal" />
          {!selectedUser && <TextField fullWidth label="Password" type="password" size={isMobile ? 'small' : 'medium'} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} margin="normal" />}
          <TextField fullWidth select label="Role" size={isMobile ? 'small' : 'medium'} value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} margin="normal">
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
            <MenuItem value="media_buyer">Media Buyer</MenuItem>
          </TextField>
          <TextField fullWidth select label="Status" size={isMobile ? 'small' : 'medium'} value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} margin="normal">
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={selectedUser ? handleUpdate : handleCreate}>{selectedUser ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} fullScreen={isMobile} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password for {selectedUser?.full_name}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="New Password" type="password" size={isMobile ? 'small' : 'medium'} value={newPassword} onChange={e => setNewPassword(e.target.value)} margin="normal" helperText="Minimum 6 characters" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword}>Change Password</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Partners Dialog */}
      <Dialog open={openTransferDialog} onClose={() => setOpenTransferDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Transfer Partners from {selectedUser?.full_name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>This will transfer ALL partners to the selected manager.</Typography>
          <FormControl fullWidth margin="normal" size={isMobile ? 'small' : 'medium'}>
            <InputLabel>Target Manager</InputLabel>
            <Select value={targetManager} onChange={(e) => setTargetManager(e.target.value)} label="Target Manager">
              {availableManagers.length === 0 ? (
                <MenuItem value="" disabled>No other managers available</MenuItem>
              ) : availableManagers.map(m => <MenuItem key={m.user_id} value={m.user_id}>{m.full_name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenTransferDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTransferPartners} disabled={!targetManager} color="warning">Transfer All Partners</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>Assign to {selectedUser?.full_name}</DialogTitle>
          <DialogContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Assign Partners ({assignedPartners.length})</Typography>
          <TextField size="small" placeholder="Search partners..." value={partnerSearch} onChange={(e) => setPartnerSearch(e.target.value)} sx={{ mb: 2, width: '100%' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: { xs: 200, sm: 300 }, overflow: 'auto' }}>
            {filteredPartners.map(p => <Chip key={p.partner_id} label={p.name} onClick={() => togglePartner(p.partner_id)} color={assignedPartners.includes(p.partner_id) ? 'primary' : 'default'} variant={assignedPartners.includes(p.partner_id) ? 'filled' : 'outlined'} size="small" />)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAssignments}>Save Assignments</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
